import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Tabs,
  Tab,
  Typography,
  Chip,
  Switch,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
  Tooltip,
  Snackbar,
  Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ImageIcon from '@mui/icons-material/Image';
import { useTranslation } from 'react-i18next';
import { accountingApi } from '../../../../../config/apiClient';
import kioskAssignmentApi, { KioskDto } from '../../../../../services/kioskAssignmentApiService';
import kioskProductVisibilityApi from '../../../../../services/kioskProductVisibilityApiService';
import kioskSignalRService, {
  KioskProductVisibilityChangedPayload,
} from '../../../../../services/kioskSignalRService';

interface KioskProductsModalProps {
  open: boolean;
  onClose: () => void;
  screenId: number;
  companyId: string;
  branchId: string;
}

interface KioskInventoryItem {
  id: number | string;
  name: string;
  stockCode?: string | null;
  categoryName?: string | null;
  brandName?: string | null;
  salePrice?: number;
  saleCurrency?: string;
  imageUrl?: string | null;
  isActive?: boolean;
  isKiosk?: boolean;
}

const KioskProductsModal: React.FC<KioskProductsModalProps> = ({
  open,
  onClose,
  screenId,
  companyId, // eslint-disable-line @typescript-eslint/no-unused-vars
  branchId,
}) => {
  const { t } = useTranslation('salesSystem');

  // Mount state
  const [kiosks, setKiosks] = useState<KioskDto[]>([]);
  const [activeKioskId, setActiveKioskId] = useState<number | null>(null);
  const [products, setProducts] = useState<KioskInventoryItem[]>([]);
  // Map<kioskId, Set<firebaseProductId>>
  const [hiddenByKiosk, setHiddenByKiosk] = useState<Map<number, Set<string>>>(new Map());

  const [loadingKiosks, setLoadingKiosks] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingHidden, setLoadingHidden] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  // Per-product saving state (productId-kioskId combo)
  const [savingIds, setSavingIds] = useState<Set<string>>(new Set());

  // Snackbar
  const [snack, setSnack] = useState<{ open: boolean; severity: 'success' | 'error'; msg: string }>({
    open: false,
    severity: 'success',
    msg: '',
  });

  // Cleanup ref for SignalR
  const cbIdRef = useRef<string>(`kpm-${screenId}-${Date.now()}`);

  /* ─────────────────────────────────────────────
   * Data load
   * ───────────────────────────────────────────── */
  const loadKiosks = useCallback(async () => {
    if (!screenId) return;
    setLoadingKiosks(true);
    setError(null);
    try {
      const list = await kioskAssignmentApi.getByScreen(screenId);
      setKiosks(list);
      if (list.length > 0) {
        setActiveKioskId((prev) => (prev && list.some((k) => k.id === prev) ? prev : list[0].id));
      } else {
        setActiveKioskId(null);
      }
    } catch (err: any) {
      console.error('[KioskProductsModal] kiosk listesi yuklenmedi:', err);
      setError(err?.response?.data?.message || err?.message || t('kioskProductsModal.errors.loadKiosksFailed'));
      setKiosks([]);
      setActiveKioskId(null);
    } finally {
      setLoadingKiosks(false);
    }
  }, [screenId, t]);

  const loadProducts = useCallback(async () => {
    if (!branchId) return;
    setLoadingProducts(true);
    try {
      const branchParam = branchId ? `?branchId=${encodeURIComponent(branchId)}` : '';
      const response = await accountingApi.get(`/api/Inventory/kiosk-items${branchParam}`);
      const result = response.data;
      if (result?.success && Array.isArray(result.data)) {
        const list: KioskInventoryItem[] = result.data
          .filter((p: any) => p.isActive && p.isKiosk)
          .map((item: any) => ({
            id: item.id,
            name: item.name || t('kioskProductsModal.unnamedProduct'),
            stockCode: item.stockCode,
            categoryName: item.categoryName,
            brandName: item.brandName,
            salePrice: item.salePrice ?? 0,
            saleCurrency: item.saleCurrency || 'TRY',
            imageUrl: item.imageUrl,
            isActive: item.isActive,
            isKiosk: item.isKiosk,
          }));
        setProducts(list);
      } else {
        setProducts([]);
      }
    } catch (err: any) {
      console.error('[KioskProductsModal] urunler yuklenmedi:', err);
      setError(err?.response?.data?.message || err?.message || t('kioskProductsModal.errors.loadProductsFailed'));
      setProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  }, [branchId, t]);

  const loadHiddenForKiosk = useCallback(
    async (kioskId: number) => {
      if (!kioskId) return;
      setLoadingHidden(true);
      try {
        const ids = await kioskProductVisibilityApi.getHiddenForKiosk(kioskId);
        setHiddenByKiosk((prev) => {
          const next = new Map(prev);
          next.set(kioskId, new Set(ids.map((s) => String(s))));
          return next;
        });
      } catch (err: any) {
        console.error('[KioskProductsModal] hidden listesi yuklenmedi:', err);
        setError(err?.response?.data?.message || err?.message || t('kioskProductsModal.errors.loadHiddenFailed'));
      } finally {
        setLoadingHidden(false);
      }
    },
    [t]
  );

  /* ─────────────────────────────────────────────
   * Lifecycle: open + active kiosk change
   * ───────────────────────────────────────────── */
  useEffect(() => {
    if (!open) return;
    loadKiosks();
    loadProducts();
  }, [open, loadKiosks, loadProducts]);

  useEffect(() => {
    if (!open || !activeKioskId) return;
    // Modal her açıldığında veya farklı kiosk sekmesine geçildiğinde fresh load
    // yapılır. Eski davranış: Map'te varsa atlanıyordu — SignalR kaçırılırsa
    // stale state oluyordu. Artık her görünür hale geldiğinde backend gerçeği
    // doğrular.
    console.log('🖥️ [KioskProductsModal] kiosk değişti / modal açıldı → fresh loadHiddenForKiosk', {
      activeKioskId,
    });
    loadHiddenForKiosk(activeKioskId);
    // hiddenByKiosk dependency'sinden çıkarıldı — aksi halde her hidden update
    // sonrası tekrar fetch ederdi (sonsuz döngü riski).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeKioskId, loadHiddenForKiosk]);

  /* ─────────────────────────────────────────────
   * SignalR live sync
   * ───────────────────────────────────────────── */
  useEffect(() => {
    if (!open) return;
    const cbId = cbIdRef.current;

    const handler = (payload: KioskProductVisibilityChangedPayload) => {
      console.log('🖥️ [KioskProductsModal] ════════════════════════════════════════');
      console.log('🖥️ [KioskProductsModal] SignalR event geldi:', payload);
      console.log('🖥️ [KioskProductsModal] payload tipleri:', {
        KioskId: typeof payload.KioskId,
        FirebaseProductId: typeof payload.FirebaseProductId,
        IsHidden: typeof payload.IsHidden,
      });
      setHiddenByKiosk((prev) => {
        const next = new Map(prev);
        const prevSet = next.get(payload.KioskId) || new Set<string>();
        const set = new Set(prevSet);
        if (payload.IsHidden) set.add(payload.FirebaseProductId);
        else set.delete(payload.FirebaseProductId);
        next.set(payload.KioskId, set);
        console.log('🖥️ [KioskProductsModal] hiddenByKiosk güncellendi:', {
          kioskId: payload.KioskId,
          önce: Array.from(prevSet),
          sonra: Array.from(set),
        });
        return next;
      });
      console.log('🖥️ [KioskProductsModal] ════════════════════════════════════════');
    };

    console.log('🖥️ [KioskProductsModal] SignalR subscription kaydediliyor, cbId=', cbId);
    kioskSignalRService.onKioskProductVisibilityChanged(cbId, handler);
    return () => {
      console.log('🖥️ [KioskProductsModal] SignalR subscription kaldırılıyor, cbId=', cbId);
      kioskSignalRService.removeCallback(cbId);
    };
  }, [open]);

  /* ─────────────────────────────────────────────
   * Toggle visibility (optimistic)
   * ───────────────────────────────────────────── */
  const handleToggle = async (productId: string, currentlyHidden: boolean) => {
    console.log('🖥️ [KioskProductsModal] ════════════════════════════════════════');
    console.log('🖥️ [KioskProductsModal] handleToggle ÇAĞRILDI:', {
      productId,
      currentlyHidden,
      activeKioskId,
      yeniDeğer: !currentlyHidden,
    });

    if (!activeKioskId) {
      console.warn('⚠️ [KioskProductsModal] activeKioskId yok, toggle iptal');
      return;
    }
    const kioskId = activeKioskId;
    const savingKey = `${kioskId}__${productId}`;

    if (savingIds.has(savingKey)) {
      console.log('ℹ️ [KioskProductsModal] zaten kaydediliyor, atlandı:', savingKey);
      return;
    }

    // Optimistic update
    setHiddenByKiosk((prev) => {
      const next = new Map(prev);
      const set = new Set(next.get(kioskId) || []);
      if (currentlyHidden) set.delete(productId);
      else set.add(productId);
      next.set(kioskId, set);
      return next;
    });

    setSavingIds((prev) => {
      const next = new Set(prev);
      next.add(savingKey);
      return next;
    });

    try {
      const payload = {
        KioskId: kioskId,
        FirebaseProductId: productId,
        IsHidden: !currentlyHidden,
      };
      console.log('🖥️ [KioskProductsModal] setVisibility API çağrılıyor:', payload);
      const startMs = Date.now();
      const result = await kioskProductVisibilityApi.setVisibility(payload);
      const elapsedMs = Date.now() - startMs;
      console.log('✅ [KioskProductsModal] setVisibility BAŞARILI:', {
        elapsed: `${elapsedMs}ms`,
        result,
      });
      console.log('🖥️ [KioskProductsModal] ════════════════════════════════════════');
      setSnack({ open: true, severity: 'success', msg: t('kioskProductsModal.toggleSuccess') });
    } catch (err: any) {
      console.error('❌ [KioskProductsModal] setVisibility HATA:', err);
      console.error('❌ [KioskProductsModal] hata detay:', {
        message: err?.message,
        response: err?.response?.data,
        status: err?.response?.status,
      });
      console.log('🖥️ [KioskProductsModal] ════════════════════════════════════════');
      // Rollback
      setHiddenByKiosk((prev) => {
        const next = new Map(prev);
        const set = new Set(next.get(kioskId) || []);
        if (currentlyHidden) set.add(productId);
        else set.delete(productId);
        next.set(kioskId, set);
        return next;
      });
      setSnack({
        open: true,
        severity: 'error',
        msg: err?.response?.data?.message || err?.message || t('kioskProductsModal.toggleError'),
      });
    } finally {
      setSavingIds((prev) => {
        const next = new Set(prev);
        next.delete(savingKey);
        return next;
      });
    }
  };

  /* ─────────────────────────────────────────────
   * Filter products by search
   * ───────────────────────────────────────────── */
  const filteredProducts = useMemo(() => {
    if (!search.trim()) return products;
    const q = search.trim().toLocaleLowerCase('tr-TR');
    return products.filter((p) => {
      const name = (p.name || '').toLocaleLowerCase('tr-TR');
      const code = (p.stockCode || '').toLocaleLowerCase('tr-TR');
      const cat = (p.categoryName || '').toLocaleLowerCase('tr-TR');
      return name.includes(q) || code.includes(q) || cat.includes(q);
    });
  }, [products, search]);

  const activeHidden = activeKioskId ? hiddenByKiosk.get(activeKioskId) || new Set<string>() : new Set<string>();
  const visibleCount = filteredProducts.length - filteredProducts.filter((p) => activeHidden.has(String(p.id))).length;
  const hiddenCount = filteredProducts.filter((p) => activeHidden.has(String(p.id))).length;

  /* ─────────────────────────────────────────────
   * Render
   * ───────────────────────────────────────────── */
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" scroll="paper">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.5 }}>
        <Inventory2Icon color="primary" />
        <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
          {t('kioskProductsModal.title')}
        </Typography>
        <Tooltip title={t('kioskProductsModal.refresh')}>
          <span>
            <IconButton
              size="small"
              onClick={() => {
                if (activeKioskId) loadHiddenForKiosk(activeKioskId);
                loadProducts();
                loadKiosks();
              }}
              disabled={loadingKiosks || loadingProducts || loadingHidden}
            >
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />

      <DialogContent dividers sx={{ p: 0 }}>
        {error && (
          <Box sx={{ p: 2 }}>
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          </Box>
        )}

        {loadingKiosks ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : kiosks.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <DesktopWindowsIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body1" color="text.secondary">
              {t('kioskProductsModal.empty')}
            </Typography>
          </Box>
        ) : (
          <>
            {/* Kiosk tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={activeKioskId ?? false}
                onChange={(_, val) => setActiveKioskId(val)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
              >
                {kiosks.map((k) => (
                  <Tab
                    key={k.id}
                    value={k.id}
                    label={k.name || `Kiosk #${k.id}`}
                    icon={<DesktopWindowsIcon fontSize="small" />}
                    iconPosition="start"
                    sx={{ minHeight: 48, textTransform: 'none' }}
                  />
                ))}
              </Tabs>
            </Box>

            {/* Search + counts */}
            <Box
              sx={{
                p: 2,
                display: 'flex',
                gap: 2,
                alignItems: 'center',
                flexWrap: 'wrap',
                bgcolor: 'background.default',
              }}
            >
              <TextField
                size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('kioskProductsModal.search')}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ flex: 1, minWidth: 220 }}
              />
              <Stack direction="row" spacing={1}>
                <Chip
                  icon={<VisibilityIcon />}
                  label={`${t('kioskProductsModal.visibleCount')}: ${visibleCount}`}
                  color="success"
                  variant="outlined"
                  size="small"
                />
                <Chip
                  icon={<VisibilityOffIcon />}
                  label={`${t('kioskProductsModal.hiddenCount')}: ${hiddenCount}`}
                  color="warning"
                  variant="outlined"
                  size="small"
                />
              </Stack>
            </Box>

            {/* Loading overlay for hidden listesi (per-tab) */}
            {(loadingProducts || loadingHidden) && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={20} />
              </Box>
            )}

            {/* Product list */}
            {filteredProducts.length === 0 && !loadingProducts ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {search ? t('kioskProductsModal.noResults') : t('kioskProductsModal.noProducts')}
                </Typography>
              </Box>
            ) : (
              <List sx={{ maxHeight: 480, overflowY: 'auto', py: 0 }}>
                {filteredProducts.map((p) => {
                  const productId = String(p.id);
                  const isHidden = activeHidden.has(productId);
                  const savingKey = activeKioskId ? `${activeKioskId}__${productId}` : '';
                  const isSaving = savingIds.has(savingKey);
                  return (
                    <React.Fragment key={productId}>
                      <ListItem
                        secondaryAction={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {isSaving && <CircularProgress size={16} />}
                            <Switch
                              checked={!isHidden}
                              onChange={(_, newChecked) => {
                                console.log('🖥️ [KioskProductsModal] Switch onChange:', {
                                  product: p.name,
                                  productId,
                                  prevIsHidden: isHidden,
                                  newCheckedValue: newChecked,
                                  // newChecked=true → "görünür yap" (currentlyHidden=true)
                                  // newChecked=false → "gizle" (currentlyHidden=false)
                                  currentlyHiddenInArg: isHidden,
                                  willGoHidden: !isHidden,
                                  isSaving,
                                  activeKioskId,
                                });
                                handleToggle(productId, isHidden);
                              }}
                              disabled={isSaving || !activeKioskId}
                              color="success"
                              inputProps={{ 'aria-label': p.name }}
                            />
                          </Box>
                        }
                      >
                        <ListItemAvatar>
                          {p.imageUrl ? (
                            <Avatar src={p.imageUrl} alt={p.name} variant="rounded" />
                          ) : (
                            <Avatar variant="rounded" sx={{ bgcolor: 'action.hover' }}>
                              <ImageIcon color="disabled" />
                            </Avatar>
                          )}
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 500,
                                  color: isHidden ? 'text.disabled' : 'text.primary',
                                  textDecoration: isHidden ? 'line-through' : 'none',
                                }}
                              >
                                {p.name}
                              </Typography>
                              {isHidden && (
                                <Chip
                                  size="small"
                                  label={t('kioskProductsModal.hidden')}
                                  color="warning"
                                  variant="outlined"
                                  sx={{ height: 20 }}
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary">
                              {[p.categoryName, p.stockCode].filter(Boolean).join(' • ')}
                            </Typography>
                          }
                        />
                      </ListItem>
                      <Divider component="li" />
                    </React.Fragment>
                  );
                })}
              </List>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1 }}>
        <Button onClick={onClose}>{t('kioskProductsModal.close')}</Button>
      </DialogActions>

      <Snackbar
        open={snack.open}
        autoHideDuration={2500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} variant="filled" onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default KioskProductsModal;
